import type { FetchBaseQueryMeta } from '@reduxjs/toolkit/query'
import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { Mutex } from 'async-mutex'

import { RootState } from 'store/rootReducer'

import { getBasketGiftParams, selectBasketGiftIds } from '@/entities/cart/lib/basket-gifts'
import { setSessionToken } from '@/entities/session'
import { refreshAuthTokens } from '@/entities/user/api/refreshAuthTokens'
import { logout } from '@/entities/user/model/services/logout'

import { API_URL } from '@/shared/lib/const/env'

import { isJwtFresh } from '../lib/utils/jwt/is-jwt-fresh'

const mutex = new Mutex()

type BaseQueryWithReauth = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>

const baseQuery = fetchBaseQuery({
	baseUrl: API_URL,
	credentials: 'include',
	mode: 'cors',
	prepareHeaders: (headers, { getState }) => {
		const { session } = getState() as RootState

		if (session.token && !headers.has('Authorization')) {
			headers.set('Authorization', `Bearer ${session.token}`)
		}

		headers.set('Accept', 'application/json')

		return headers
	}
})

// fetchBaseQuery returns meta.request (an Undici Request) and meta.response (an Undici Response).
// RTK Query stores that meta on every fulfilled action; on the server this keeps the Undici Request
// reachable, so its internal AbortController never finalizes and the whole request's AbortSignal +
// listener + SSR context stay alive → RSS climbs until PM2 OOM-restarts (see __docs__/heap). Strip
// the Undici objects, keeping only the primitive HTTP status that consumers read (e.g. flpApi).
const slimMeta = (result: Awaited<ReturnType<typeof baseQuery>>): Awaited<ReturnType<typeof baseQuery>> => {
	if (!result.meta) {
		return result
	}

	const { response } = result.meta

	return {
		...result,
		meta: (response
			? { response: { status: response.status, statusText: response.statusText } }
			: {}) as unknown as FetchBaseQueryMeta
	}
}

const baseQueryWithReauth: BaseQueryWithReauth = async (args, api, extraOptions) => {
	const { session } = api.getState() as RootState
	const request = typeof args === 'string' ? { url: args } : args
	if (request.url === 'basket' && (!request.method || request.method === 'GET')) {
		args = { ...request, params: getBasketGiftParams(selectBasketGiftIds(api.getState() as RootState)) }
	}

	await mutex.waitForUnlock()
	let response = await baseQuery(args, api, extraOptions)

	const resCondition = response?.error?.status === 401
	const jwtCondition = session.token ? !isJwtFresh(session.token) : false
	const shouldRefresh = resCondition || jwtCondition

	if (shouldRefresh) {
		if (!mutex.isLocked()) {
			const release = await mutex.acquire()

			try {
				const { data } = await refreshAuthTokens()
				const token = data?.ACCESS_TOKEN

				if (token) {
					api.dispatch(setSessionToken(token))
					response = await baseQuery(args, api, extraOptions)
				} else {
					api.dispatch(logout())
				}
			} catch (error) {
				api.dispatch(logout())
			} finally {
				release()
			}
		} else {
			await mutex.waitForUnlock()
			response = await baseQuery(args, api, extraOptions)
		}
	}

	return slimMeta(response)
}

export const apiSlice = createApi({
	reducerPath: 'api',
	baseQuery: baseQueryWithReauth,
	tagTypes: ['Order', 'Cart'],
	keepUnusedDataFor: 60,
	refetchOnFocus: true,
	endpoints: () => ({})
})
